/**
 * Detail Book / Net Exposure workstation — Backend-UI visual + flow clone.
 * Spec: docs/superpowers/specs/2026-08-03-net-exposure-detail-book-workstation-design.md
 * Parity: docs/superpowers/specs/2026-08-03-net-exposure-detail-book-parity-map.md
 *
 * Live (Lab workstation.js): ForGroup getTotalBooks1 / getBooksForBackend /
 * getMemberNetExposure / getBetDetailsForWorkstation via `useWorkstationBook`.
 * Live odds: Socket-API SSE (Lab rateSocket batb/batl). Demo → fixture.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@w7/ui'
import { useThemePrefs } from '@/app/theme'
import {
  useWorkstationBook,
  type WorkstationBetFilters,
} from '@/api/hooks/net-exposure/useWorkstation'
import { BOOK_TYPES, type BookType } from './fixture'
import {
  defaultBetDateRange,
  marketTitleFor,
  parseWorkstationSearch,
} from './workstation-fixture'
import { BetBreakdown } from './workstation/BetBreakdown'
import { BookTree } from './workstation/BookTree'
import { OddsPanel } from './workstation/OddsPanel'
import './workstation/workstation.css'

export function WorkstationPage() {
  const { marketId = '' } = useParams<{ marketId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { theme, neCards } = useThemePrefs()
  /** Header / market bar / bets follow portal theme. */
  const shellSurface = theme
  /** Odds + hierarchy cards follow Net Exposure cards toggle (Agents parity). */
  const bookSurface = neCards
  const parsed = useMemo(() => parseWorkstationSearch(searchParams), [searchParams])
  const bookType = parsed.book
  const betRange = useMemo(() => defaultBetDateRange(), [])
  const [betFilters, setBetFilters] = useState<WorkstationBetFilters>(() => ({
    page: 1,
    maxRow: 100,
    fromDate: betRange.fromDate,
    toDate: betRange.toDate,
    currencyType: 'All',
  }))

  const {
    book,
    isLoading,
    isError,
    error,
    isFetching,
    live,
    betCount,
    refetch,
    ratesStatus,
  } = useWorkstationBook({
    marketId,
    selectedType: bookType,
    eventName: parsed.event,
    marketName: parsed.name,
    type: parsed.type,
    ...betFilters,
  })

  const eventName = parsed.event || book?.eventName || 'Market'
  const marketName = parsed.name || book?.marketName || ''
  const type = book?.type ?? parsed.type
  const title = marketTitleFor(type, marketName)

  const setBook = (next: BookType) => {
    const params = new URLSearchParams(searchParams)
    params.set('book', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="w7-ws" data-ws-surface={shellSurface}>
      <header className="w7-ws-header">
        <button
          type="button"
          className="w7-ws-back"
          aria-label="Back to Net Exposure"
          onClick={() => navigate('/book-management/net-exposure')}
        >
          ‹
        </button>
        <h1 className="w7-ws-event">{eventName}</h1>
        <span className="w7-spacer" />
        {live ? (
          <Button
            variant="secondary"
            icon="refresh"
            busy={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        ) : null}
        {live && ratesStatus === 'open' ? (
          <span className="w7-ws-live-odds" title="Live odds stream connected">
            Live odds
          </span>
        ) : null}
      </header>

      <div className="w7-ws-market-bar">
        <h2 className="w7-ws-market-title">{title}</h2>
        <div className="w7-ws-book-radios" role="radiogroup" aria-label="Book type">
          {BOOK_TYPES.map((typeOption) => (
            <label key={typeOption} className="w7-ws-book-radio">
              <input
                type="radio"
                name="ws-book-type"
                value={typeOption}
                checked={bookType === typeOption}
                onChange={() => setBook(typeOption)}
              />
              <span>{typeOption}</span>
            </label>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="w7-ws-empty">Loading Detail Book…</p>
      ) : isError ? (
        <p className="w7-ws-empty">
          Could not load books
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </p>
      ) : (
        <>
          <div className="w7-ws-body">
            <section
              className="w7-ws-odds-card w7-ws-book"
              data-ws-surface={bookSurface}
              aria-label="Odds"
            >
              {book ? (
                <OddsPanel book={book} bookType={bookType} />
              ) : (
                <p className="w7-ws-empty">
                  {live ? 'No open book for this market.' : 'No fixture for this market yet.'}
                </p>
              )}
            </section>
            <section
              className="w7-ws-ne-card w7-ws-book"
              data-ws-surface={bookSurface}
              aria-label="Net exposure breakdown"
            >
              {book ? (
                <BookTree book={book} bookType={bookType} live={live} />
              ) : (
                <p className="w7-ws-empty">
                  {live ? 'No open book for this market.' : 'No fixture for this market yet.'}
                </p>
              )}
            </section>
          </div>

          {book ? (
            <BetBreakdown
              book={book}
              surface={shellSurface}
              live={live}
              betCount={betCount}
              betFilters={live ? betFilters : undefined}
              onBetFiltersChange={live ? setBetFilters : undefined}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
